import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { RefreshCcw } from "lucide-react";

const SITE_URL = "https://healic.in";

export const Route = createFileRoute("/return-and-exchange")({
  head: () => ({
    meta: [
      { title: "Return and Exchange Policy — Healic" },
      {
        name: "description",
        content:
          "Understand Healic's return and exchange policy for diagnostics, pharmacy products, and healthcare services.",
      },
      { property: "og:title", content: "Return and Exchange Policy — Healic" },
      {
        property: "og:description",
        content:
          "Return and exchange guidelines for Healic healthcare products and services.",
      },
      { property: "og:url", content: `${SITE_URL}/return-and-exchange` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/return-and-exchange` }],
  }),
  component: ReturnExchangePage,
});

const sections = [
  {
    id: "overview",
    title: "Overview",
    content: [
      "At Healic, we are committed to ensuring your satisfaction with our healthcare services, diagnostics, and pharmacy products. This Return and Exchange Policy outlines the conditions under which returns, exchanges, and refunds may be requested.",
      "Different services and products have different return eligibility. Please review the specific sections below for details.",
    ],
  },
  {
    id: "surgery-packages",
    title: "Surgery and Procedure Packages",
    content: [
      "Surgery packages at Healic are offered at fixed, transparent prices. Due to the nature of healthcare services, returns and exchanges are handled as follows:",
    ],
    bullets: [
      "Cancellation at least 48 hours before the scheduled procedure: Full refund minus any non-refundable deposits or pre-procedure diagnostics already performed",
      "Cancellation within 24-48 hours of the procedure: 75% refund minus non-refundable costs",
      "Cancellation within 24 hours of the procedure: 50% refund minus non-refundable costs",
      "No-shows without prior notice: No refund; package amount is forfeited",
      "If Healic cancels the procedure due to medical unsuitability determined by our doctors: Full refund including deposits",
    ],
  },
  {
    id: "diagnostics",
    title: "Diagnostics and Lab Tests",
    content: [
      "For diagnostic tests and laboratory services:",
    ],
    bullets: [
      "Cancellation before sample collection or procedure: Full refund",
      "Cancellation after sample collection but before report generation: Refund of 50% of test cost",
      "Once reports are generated: No refund; however, a re-test may be offered at a discounted rate if there are valid concerns about test accuracy",
      "Incorrect test booked by patient: Exchange to the correct test is allowed within 7 days; price difference to be paid or refunded as applicable",
    ],
  },
  {
    id: "pharmacy",
    title: "Pharmacy Products",
    content: [
      "For medicines and healthcare products purchased through Healic Pharmacy:",
    ],
    bullets: [
      "Unopened, unused products in original packaging: Return or exchange within 7 days of purchase with valid bill/receipt",
      "Damaged or defective products: Full refund or exchange within 7 days; product will be inspected before approval",
      "Temperature-sensitive items (refrigerated medicines, vaccines): Returns only accepted if packaging is intact and cold chain has not been broken; must be reported within 24 hours",
      "Prescription medicines: Returns accepted only if wrong medicine was dispensed against the prescription; original prescription and bill required",
      "Opened, used, or partially consumed products: Not eligible for return or exchange",
    ],
  },
  {
    id: "opd-consultations",
    title: "OPD Consultations",
    content: [
      "For outpatient doctor consultations:",
    ],
    bullets: [
      "Cancellation at least 24 hours before the appointment: Full refund or free rescheduling",
      "Cancellation within 24 hours: Rescheduling allowed; no refund for paid consultations",
      "No-shows without notice: Consultation fee is forfeited",
      "Dissatisfaction with consultation: A review will be conducted; a re-consultation with another doctor may be offered at no additional charge based on the outcome",
    ],
  },
  {
    id: "process",
    title: "Return and Exchange Process",
    content: [
      "To initiate a return or exchange:",
    ],
    bullets: [
      "Contact our support team at support@healic.in or call our helpline within the applicable time window",
      "Provide your order/appointment ID, reason for return, and any supporting documentation (photos for damaged items, prescriptions, etc.)",
      "Our team will review and respond within 2 business days",
      "Approved returns: Refund will be processed to the original payment method within 7-14 business days",
      "Approved exchanges: Replacement product or rescheduled service will be confirmed within 2 business days",
    ],
  },
  {
    id: "exceptions",
    title: "Non-Returnable Items and Services",
    content: [
      "The following are not eligible for return or exchange:",
    ],
    bullets: [
      "Services already rendered or partially rendered (e.g., surgery completed, diagnostics performed)",
      "Consumable medical supplies that have been opened or used",
      "Personal care and hygiene products",
      "Custom-ordered or specially procured medicines and devices",
      "Services availed through third-party partners not directly managed by Healic",
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    content: [
      "For any questions regarding returns, exchanges, or refunds, please contact us:",
    ],
    bullets: [
      "Email: support@healic.in",
      "Phone: 92669 80080",
      "Working Hours: Monday to Sunday, 7 AM to 12 PM",
    ],
  },
];

function ReturnExchangePage() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
        <div className="relative mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-14">
          <div className="flex items-center gap-2">
            <RefreshCcw className="h-5 w-5 text-accent" />
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">
              Policy
            </p>
          </div>
          <h1 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
            Return and Exchange Policy
          </h1>
          <p className="mt-3 max-w-3xl text-sm opacity-85 sm:text-base">
            Our guidelines for returns, exchanges, and refunds across surgery
            packages, diagnostics, pharmacy products, and consultations.
          </p>
          <p className="mt-4 text-xs opacity-70">
            Last Updated: June 13, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-4xl px-4 py-10 lg:px-8 lg:py-14">
        <div className="space-y-10">
          {sections.map((s) => (
            <div
              key={s.id}
              id={s.id}
              className="rounded-3xl border border-border bg-card p-6 shadow-soft lg:p-8"
            >
              <h2 className="font-display text-xl font-bold sm:text-2xl">
                {s.title}
              </h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
                {s.content.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              {s.bullets && (
                <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-foreground/85">
                  {s.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
